import { findConsecutiveRepeatedCharacter, findRepeatedWord } from './descriptionValidation';

export interface TimezoneExistingRecord {
  _id?: string;
  code?: string;
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
  metadata?: Record<string, unknown>;
}

export interface TimezoneFormInput {
  name: string;
  description?: string;
  sortOrder: number;
  metadata: Record<string, string | number | boolean>;
}

export const TIMEZONE_FIELD_LABELS: Record<string, string> = {
  ianaId: 'IANA timezone',
  name: 'Timezone name',
  sortOrder: 'Sort order',
  description: 'Description',
};

export const TIMEZONE_MAX_SORT_ORDER = 9999;

const NAME_PATTERN = /^[\p{L}\p{M}\p{N}][\p{L}\p{M}\p{N} .'()\-–/+:,]*$/u;
const str = (v: unknown) => (v == null ? '' : String(v).trim());
export const normalizeTimezoneName = (s: unknown) => String(s ?? '').replace(/\s+/g, ' ').trim();

/** Current UTC offset of an IANA zone, e.g. "+05:30" (DST-aware). */
export function currentOffset(ianaId: string, at = new Date()): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: ianaId, timeZoneName: 'longOffset' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName')?.value || 'GMT';
    const m = /GMT([+-]\d{2}:\d{2})/.exec(part);
    return m ? m[1] : '+00:00';
  } catch {
    return '';
  }
}

export function localTime(ianaId: string, at = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: ianaId, weekday: 'short', hour: '2-digit', minute: '2-digit' }).format(at);
  } catch {
    return '';
  }
}

export function nextTimezoneSortOrder(existing: TimezoneExistingRecord[], editingId?: string) {
  const used = existing.filter((r) => r._id !== editingId).map((r) => Number(r.sortOrder) || 0);
  return Math.max(0, ...used) + 1;
}

function repeatedTextError(label: string, text: string) {
  const run = findConsecutiveRepeatedCharacter(text);
  if (run) return `${label} cannot repeat the same character 4+ times ("${run.slice(0, 4)}")`;
  const word = findRepeatedWord(text);
  if (word) return `${label} cannot repeat the same word back to back ("${word}")`;
  return undefined;
}

/** Field errors keyed by field name (metadata keys are flattened). */
export function validateTimezoneForm(
  form: TimezoneFormInput,
  ctx: { existing: TimezoneExistingRecord[]; editingId?: string; knownZone: boolean },
): Record<string, string> {
  const errors: Record<string, string> = {};
  const others = ctx.existing.filter((r) => r._id !== ctx.editingId);
  const ianaId = str(form.metadata.ianaId);

  if (!ianaId || !ctx.knownZone) errors.ianaId = 'Select a timezone from the IANA list';
  else {
    const clash = others.find((r) => str(r.metadata?.ianaId).toLowerCase() === ianaId.toLowerCase());
    if (clash) errors.ianaId = `${ianaId} is already added (${clash.name})`;
  }

  const name = normalizeTimezoneName(form.name);
  if (!name) errors.name = 'Timezone name is required';
  else if (name.length < 2 || name.length > 80) errors.name = 'Timezone name must be 2–80 characters';
  else if (!NAME_PATTERN.test(name)) errors.name = 'Start with a letter or digit; use letters, digits, spaces or . \' ( ) - – / + : ,';
  else {
    const repeat = repeatedTextError('Timezone name', name);
    if (repeat) errors.name = repeat;
    else {
      const clash = others.find((r) => normalizeTimezoneName(r.name).toLowerCase() === name.toLowerCase());
      if (clash) errors.name = `"${name}" is already used by ${str(clash.metadata?.ianaId) || clash.code}`;
    }
  }

  const order = Number(form.sortOrder);
  if (!Number.isInteger(order) || order < 1 || order > TIMEZONE_MAX_SORT_ORDER) {
    errors.sortOrder = `Enter a whole number between 1 and ${TIMEZONE_MAX_SORT_ORDER}`;
  } else {
    const clash = others.find((r) => Number(r.sortOrder) === order);
    if (clash) {
      errors.sortOrder = `Sort order ${order} is already used by ${clash.name} — next available is ${nextTimezoneSortOrder(ctx.existing, ctx.editingId)}`;
    }
  }

  const description = str(form.description);
  if (description.length > 500) errors.description = 'Description must be at most 500 characters';
  else if (description) {
    const repeat = repeatedTextError('Description', description);
    if (repeat) errors.description = repeat;
  }
  return errors;
}
